import { Document } from '@talon/web/lib/api';
import { Anchor, Box, BoxProps, CheckBox, Text } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useUndoableDelete } from '../../../utils/useUndoableDelete';
import { EditableText, LineItemDelete, TalonCard } from '..';
import { useProjectStore } from '../../../context';
import { DocumentLink } from './DocumentLink';
import { DocumentUploadModal } from './DocumentUploadModal';

export type DocumentsPanelProps = {
  cardProps?: BoxProps;
  headerBackground?: string;
};

export const DocumentsPanel = observer(({ cardProps, headerBackground }: DocumentsPanelProps) => {
  const projectStore = useProjectStore();
  const [documents, setDocuments] = useState<Document[]>(projectStore.currentProject?.documents || []);
  const [, setCurrentDocument] = useState<Document | undefined>(undefined);
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [hidden, setHidden] = useState<string[]>([]);
  const deleteWithUndo = useUndoableDelete();

  useEffect(() => {
    const projectId = projectStore.currentProject?.id;
    if (!projectId) return;
    setDocuments(projectStore.currentProject?.documents || []);
  }, [projectStore.currentProject?.id, projectStore.currentProject?.documents]);

  const deleteDocuments = async () => {
    if (!selected.length) return;
    const deleted: string[] = [];
    for (const id of selected) {
      if (await projectStore.deleteDocument(id)) deleted.push(id);
    }
    setDocuments((current) => current.filter((document) => !deleted.includes(document.id)));
    setCurrentDocument(undefined);
    setSelected([]);
  };

  const updateDocument = async (update: Document) => {
    const document = await projectStore.updateDocument(update.id, update);
    if (!document) return;
    const newDocuments = documents.map((d) => (d.id === document.id ? document : d));
    setDocuments(newDocuments);
  };

  const newDoc = () => {
    setShowUpload(true);
  };

  const choose = (id: string) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((s) => s !== id));
      setCurrentDocument(undefined);
    } else {
      setSelected([...selected, id]);
      setCurrentDocument(documents.find((d) => d.id === id));
    }
  };
  const selectedNames = () => {
    return selected.map((s) => documents.find((item) => item.id === s)?.name).join(', ');
  };

  return (
    <TalonCard
      newItem={newDoc}
      deleteItems={deleteDocuments}
      label="Document"
      labelPlural="Documents"
      addButtonLabel="Upload document"
      selectedNames={selectedNames()}
      selectedCount={selected.length}
      cardProps={cardProps}
      headerBackground={headerBackground}
    >
      {showUpload && <DocumentUploadModal setShowUpload={setShowUpload} />}
      <Box direction="column" overflow={{ vertical: 'auto' }} align="start" gap="small" pad="small">
        {documents.map((doc) => !hidden.includes(doc.id) && (
          <Box direction="row" align="center" gap="small" key={doc.id} className="line-item" fill="horizontal">
            <CheckBox checked={selected.includes(doc.id)} onChange={() => choose(doc.id)} />
            <Anchor onClick={() => setCurrentDocument(doc)}>
              <DocumentLink doc={doc} />
            </Anchor>
            <Text size="xsmall" color="dark-5">
              {doc.type}
            </Text>
            <EditableText val={doc.name} onChange={(name) => updateDocument({ ...doc, name })} />
            <Text size="xsmall" color="dark-5">
              {new Date(doc.created_at).toLocaleString()}
            </Text>
            <Box flex />
            <LineItemDelete
              name={doc.name}
              onClick={() =>
                deleteWithUndo({
                  title: doc.name,
                  hide: () => {
                    setHidden((ids) => [...ids, doc.id]);
                    setSelected((ids) => ids.filter((id) => id !== doc.id));
                  },
                  restore: () => setHidden((ids) => ids.filter((id) => id !== doc.id)),
                  commit: async () => {
                    const deleted = await projectStore.deleteDocument(doc.id);
                    if (deleted) setHidden((ids) => ids.filter((id) => id !== doc.id));
                    return deleted;
                  },
                })
              }
            />
          </Box>
        ))}
      </Box>
    </TalonCard>
  );
});
